import { useMemo } from "react";
import { Panel } from "rsuite";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaAndroid,
  FaApple,
  FaWindows,
  FaLinux,
  FaPowerOff,
  FaBatteryQuarter,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";
import dev from "../models/joinDevice.json";
import "./DeviceListPanels.css";

const typeIcon = (type) => {
  switch (type) {
    case dev.types.android:
      return <FaAndroid />;
    case dev.types.apple:
      return <FaApple />;
    case dev.types.windows:
      return <FaWindows />;
    default:
      return <FaLinux />;
  }
};

const toSeconds = (timeon = "") => {
  const parts = String(timeon).split(":").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return -1;
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
};

/* Devices that are offline or low on battery */
export const NeedsAttentionPanel = ({ devices = [] }) => {
  const [t] = useTranslation();

  const items = useMemo(() => {
    return devices
      .map((d) => {
        const battery = parseInt(d.battery);
        const offline = d.state !== dev.typestates.on;
        const lowBattery = !isNaN(battery) && battery <= 25;
        if (!offline && !lowBattery) return null;
        return {
          device: d,
          reason: offline ? "offline" : "low-battery",
        };
      })
      .filter(Boolean);
  }, [devices]);

  return (
    <Panel className="content dlp-panel">
      <div className="dlp-header">{t("home.needs-attention")}</div>
      {items.length === 0 ? (
        <div className="dlp-empty">
          <FaCheckCircle /> {t("home.all-good")}
        </div>
      ) : (
        <ul className="dlp-list">
          {items.map(({ device, reason }) => (
            <li key={device.id} className="dlp-row">
              <Link to="/dashboard" className="dlp-link">
                <span className="dlp-os">{typeIcon(device.type)}</span>
                <span className="dlp-name">
                  <span className="dlp-name__main">
                    {device.sysname || device.id}
                  </span>
                  <span className="dlp-name__sub">{device.id}</span>
                </span>
                <span className={`dlp-badge dlp-badge--${reason}`}>
                  {reason === "offline" ? (
                    <FaPowerOff />
                  ) : (
                    <FaBatteryQuarter />
                  )}
                  {reason === "offline"
                    ? t("home.offline")
                    : t("home.low-battery")}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

/* Devices sorted by last response (most recent first) */
export const RecentDevicesPanel = ({ devices = [] }) => {
  const [t] = useTranslation();

  const items = useMemo(() => {
    return [...devices]
      .sort((a, b) => toSeconds(a[dev.timeon]) - toSeconds(b[dev.timeon]))
      .slice(0, 6);
  }, [devices]);

  return (
    <Panel className="content dlp-panel">
      <div className="dlp-header">{t("home.recently-seen")}</div>
      {items.length === 0 ? (
        <div className="dlp-empty">{t("_common.empty-message")}</div>
      ) : (
        <ul className="dlp-list">
          {items.map((device) => (
            <li key={device.id} className="dlp-row">
              <Link to="/dashboard" className="dlp-link">
                <span
                  className={`dlp-dot ${
                    device.state === dev.typestates.on
                      ? "dlp-dot--on"
                      : "dlp-dot--off"
                  }`}
                />
                <span className="dlp-os">{typeIcon(device.type)}</span>
                <span className="dlp-name">
                  <span className="dlp-name__main">
                    {device.sysname || device.id}
                  </span>
                  <span className="dlp-name__sub">{device.usrname}</span>
                </span>
                <span className="dlp-time">
                  <FaClock />
                  {device[dev.timeon]}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
